import { useState } from "react";
import { toast } from "sonner";
import { Download, Upload, Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useContent } from "@/context/ContentContext";

const CHUNK = 4 * 1024 * 1024;
const LABELS = { site_content: "Textes & images du site", media_files: "Fichiers médias", menus: "Menus & cartes", gallery: "Galerie", testimonials: "Témoignages", references: "Références", blog_posts: "Articles" };
const card = "rounded-2xl border border-[#2C2C2C]/10 bg-white p-6 lg:p-8";
const btn = "inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium transition-colors disabled:opacity-50";

function ExportCard() {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const r = await api.get("/admin/export", { responseType: "blob" });
      const name = (r.headers["content-disposition"] || "").match(/filename="(.+)"/)?.[1] || "afro-regal-contenu.json";
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = name;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 2000);
      toast.success("Export téléchargé");
    } catch (e) { toast.error("L'export a échoué"); }
    finally { setBusy(false); }
  };
  return (
    <div className={card} data-testid="transfer-export-card">
      <h2 className="font-serif text-2xl text-[#2C2C2C]">1. Exporter le contenu</h2>
      <p className="mt-2 text-sm text-[#2C2C2C]/65">Télécharge un fichier contenant les textes, images du site, médias, menus, galerie, témoignages, références et articles de ce site. Les demandes de devis et les comptes admin ne sont pas inclus.</p>
      <button onClick={run} disabled={busy} data-testid="transfer-export-btn" className={`${btn} mt-6 bg-[#2C2C2C] text-white hover:bg-[#C9A84C] hover:text-[#2C2C2C]`}>
        {busy ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />} {busy ? "Préparation…" : "Exporter le contenu"}
      </button>
    </div>
  );
}

function ImportCard() {
  const { refresh } = useContent();
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(null);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!file || !window.confirm("Le contenu de ce site (textes, images, menus, galerie, témoignages, références, articles) va être remplacé par celui du fichier. Les demandes de devis et les comptes admin ne seront pas touchés. Continuer ?")) return;
    setResult(null);
    try {
      const total = Math.ceil(file.size / CHUNK);
      const { data: { upload_id } } = await api.post("/admin/import/start");
      for (let i = 0; i < total; i++) {
        const fd = new FormData();
        fd.append("upload_id", upload_id);
        fd.append("index", i);
        fd.append("chunk", file.slice(i * CHUNK, (i + 1) * CHUNK), "part");
        await api.post("/admin/import/chunk", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setProgress(Math.round(((i + 1) / total) * 90));
      }
      const r = await api.post("/admin/import/finish", { upload_id, total });
      setProgress(100);
      setResult(r.data);
      await refresh();
      toast.success("Contenu importé");
    } catch (e) {
      setProgress(null);
      toast.error(formatApiError(e.response?.data?.detail) || "L'import a échoué");
    }
  };

  const busy = progress !== null && progress < 100;
  return (
    <div className={card} data-testid="transfer-import-card">
      <h2 className="font-serif text-2xl text-[#2C2C2C]">2. Importer le contenu</h2>
      <p className="mt-2 text-sm text-[#2C2C2C]/65">À faire dans l'admin du site publié : choisissez le fichier exporté pour y copier tout le contenu.</p>
      <p className="mt-4 flex items-start gap-2 rounded-xl bg-[#FAF6EF] p-4 text-sm text-[#2C2C2C]/80"><AlertTriangle size={16} className="mt-0.5 shrink-0 text-[#C9A84C]" /> Le contenu actuel de ce site sera remplacé. Les demandes de devis et les comptes admin restent intacts.</p>
      <div className="mt-6 flex flex-wrap items-center gap-4">
        <label className={`${btn} cursor-pointer border border-[#2C2C2C]/20 text-[#2C2C2C] hover:border-[#C9A84C]`}>
          <Upload size={16} /> {file ? file.name : "Choisir le fichier"}
          <input type="file" accept=".json,application/json" className="hidden" disabled={busy} data-testid="transfer-import-file" onChange={(e) => { setFile(e.target.files[0] || null); setProgress(null); setResult(null); }} />
        </label>
        <button onClick={run} disabled={!file || busy} data-testid="transfer-import-btn" className={`${btn} bg-[#C9A84C] text-[#2C2C2C] hover:bg-[#2C2C2C] hover:text-white`}>
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} Importer le contenu
        </button>
      </div>
      {progress !== null && (
        <div className="mt-5 h-2 overflow-hidden rounded-full bg-[#FAF6EF]" data-testid="transfer-import-progress">
          <div className="h-full bg-[#C9A84C] transition-[width] duration-300" style={{ width: `${progress}%` }} />
        </div>
      )}
      {result && (
        <div className="mt-5 rounded-xl border border-green-200 bg-green-50 p-4 text-sm text-green-800" data-testid="transfer-import-result">
          <p className="flex items-center gap-2 font-medium"><CheckCircle2 size={16} /> Import terminé</p>
          <ul className="mt-2 grid gap-1 sm:grid-cols-2">
            {Object.entries(result.counts).map(([k, v]) => <li key={k}>{LABELS[k] || k} : {v}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function AdminTransfert() {
  return (
    <div data-testid="admin-transfert">
      <h1 className="font-serif text-3xl text-[#2C2C2C]">Transfert de contenu</h1>
      <p className="mt-1 max-w-3xl text-sm text-[#2C2C2C]/60">L'aperçu et le site publié ont chacun leur propre base de données. Pour publier des modifications faites dans l'aperçu : exportez ici depuis l'aperçu, puis importez le fichier dans l'admin du site publié.</p>
      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        <ExportCard />
        <ImportCard />
      </div>
    </div>
  );
}
